/**
 * S-1 Druckversuchsregister (Server: Agent D, Branch `d-s1-druck`, Vertrag
 * `docs/druck/s1-druckversuchsregister.md` Abschnitte 9 und 12): Vor jedem nativen Druck reserviert
 * die Kasse einen Versuch; der Server entscheidet Original oder Kopie, liefert den Bon und bindet
 * den Versuch an Schlüssel, Gerät und Inhalt. Danach meldet die Kasse das Ergebnis.
 *
 * Stand 04.11.2026 (F6-Kandidat, Branch `feature/f6-nativdruck`): Der Server registriert die Routen
 * nur mit `BILANX_NATIVDRUCK=1` und Schema 0095 und meldet das im Status (`nativdruck: true`). Nur
 * dann bindet `App.tsx` diese Quelle ein (`nativeBonQuelle`); ohne Signal bleibt sie null und der
 * Bon läuft wie bisher über die Hardware-Bridge. F6 selbst gibt nur der Nutzer frei.
 * Gegen die echte API getestet: `apps/kasse/test/geraet/druckversuch-s1.integration.test.ts`.
 */
import { BONMODELL_FORMAT, pruefeBonmodell } from './bonmodell';
import { SICHER_NICHT_AUSGEFUEHRT, type Bonmodell, type GeraetFehler } from './bridge';

/** Minimaler HTTP-Zugang mit dem Kassenschlüssel der eigenen Kasse (z. B. aus der KassenApi). */
export interface DruckversuchHttp {
  post(pfad: string, body: unknown): Promise<{ status: number; daten: unknown }>;
}

export interface Druckversuch {
  versuchId: string;
  /** Server-Entscheidung; steckt auch in `bon.kopie`. */
  kopie: boolean;
  /** Gleicher Schlüssel, gleiches Gerät, noch offen: dieselbe Antwort erneut. */
  wiederholt: boolean;
  /** SHA-256 hex über `kanonisch(bon)`; geht mit der Meldung zurück. */
  inhaltHash: string;
  bon: Bonmodell;
}

export type Reservierung =
  | { ok: true; versuch: Druckversuch }
  /** Nicht drucken. `code` vom Server (z. B. `versuch_abgeschlossen`) oder vom Adapter. */
  | { ok: false; code: string; text: string };

export type DruckversuchErgebnis = 'gedruckt' | 'unklar' | 'nicht_ausgefuehrt';

export interface Druckmeldung {
  ergebnis: DruckversuchErgebnis;
  code?: string;
  text?: string;
}

export interface DruckversuchQuelle {
  readonly art: 'druckversuch';
  reserviere(
    kassenId: string,
    belegnummer: string,
    opt: { schluessel: string; kopie: boolean },
  ): Promise<Reservierung>;
  /**
   * Wirft nie; eine gescheiterte Meldung lässt den Versuch „reserviert“ (nächster Druck = Kopie).
   * Weist der Server die Meldung als ungültig ab (422), folgt einmal die schlichte Meldung
   * `unklar` ohne Code und Text – nie ein günstigerer Endzustand.
   */
  melde(
    kassenId: string,
    belegnummer: string,
    versuchId: string,
    inhaltHash: string,
    m: Druckmeldung,
  ): Promise<void>;
}

export function druckversuchPfad(kassenId: string, belegnummer: string): string {
  return `/kassen/${encodeURIComponent(kassenId)}/belege/${encodeURIComponent(belegnummer)}/druckversuch`;
}

export function druckmeldungPfad(kassenId: string, belegnummer: string, versuchId: string): string {
  return `${druckversuchPfad(kassenId, belegnummer)}/${encodeURIComponent(versuchId)}/meldung`;
}

/** Kanonische JSON-Darstellung wie `kanonisch` am Server (Schlüssel sortiert, undefined entfällt). */
export function kanonisch(wert: unknown): string {
  if (wert === null || typeof wert !== 'object') {
    return JSON.stringify(wert);
  }
  if (Array.isArray(wert)) {
    return `[${wert.map((w: unknown) => kanonisch(w === undefined ? null : w)).join(',')}]`;
  }
  const o = wert as Record<string, unknown>;
  const teile = Object.keys(o)
    .filter((k) => o[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${kanonisch(o[k])}`);
  return `{${teile.join(',')}}`;
}

/** SHA-256 hex über `kanonisch(bon)`; null, wenn WebCrypto fehlt. */
export async function bonHash(bon: Bonmodell): Promise<string | null> {
  const subtle = (globalThis as { crypto?: { subtle?: SubtleCrypto } }).crypto?.subtle;
  if (subtle === undefined) {
    return null;
  }
  const bytes = new TextEncoder().encode(kanonisch(bon));
  const digest = new Uint8Array(await subtle.digest('SHA-256', bytes));
  return Array.from(digest, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Bonmodell mit sortierten Schlüsseln (gleiche Reihenfolge wie `kanonisch`). */
export function kanonischesBonmodell(bon: Bonmodell): Bonmodell {
  return JSON.parse(kanonisch(bon)) as Bonmodell;
}

const HEX64 = /^[0-9a-f]{64}$/;

function leseBon(roh: unknown): Bonmodell | null {
  if (typeof roh !== 'object' || roh === null) {
    return null;
  }
  const o = roh as Record<string, unknown>;
  const erlaubt = new Set(['format', 'zeilen', 'qrCode', 'kopie']);
  if (Object.keys(o).some((k) => !erlaubt.has(k)) || o.format !== BONMODELL_FORMAT) {
    return null;
  }
  if (!Array.isArray(o.zeilen) || !o.zeilen.every((z) => typeof z === 'string')) {
    return null;
  }
  if (o.qrCode !== undefined && typeof o.qrCode !== 'string') {
    return null;
  }
  if (o.kopie !== undefined && o.kopie !== true) {
    return null;
  }
  const bon: Bonmodell = {
    format: BONMODELL_FORMAT,
    zeilen: o.zeilen,
    ...(o.qrCode === undefined ? {} : { qrCode: o.qrCode }),
    ...(o.kopie === true ? { kopie: true } : {}),
  };
  return pruefeBonmodell(bon) === null ? bon : null;
}

/** Antwort des Servers prüfen; alles Unerwartete heißt: nicht drucken. */
export async function leseDruckversuch(daten: unknown): Promise<Reservierung> {
  const ungueltig = (text: string): Reservierung => ({
    ok: false,
    code: 'antwort_ungueltig',
    text,
  });
  if (typeof daten !== 'object' || daten === null || 'auftragId' in daten) {
    return ungueltig('Server lieferte keinen Druckversuch');
  }
  const o = daten as Record<string, unknown>;
  if (typeof o.versuchId !== 'string' || o.versuchId === '' || typeof o.kopie !== 'boolean') {
    return ungueltig('Druckversuch unvollständig');
  }
  if (typeof o.inhaltHash !== 'string' || !HEX64.test(o.inhaltHash)) {
    return ungueltig('Prüfsumme fehlt');
  }
  const bon = leseBon(o.bon);
  if (bon === null) {
    return ungueltig('Server lieferte keinen druckbaren Bon');
  }
  // Kopie-Entscheidung und Bon müssen übereinstimmen – sonst fehlt „KOPIE“ auf dem Papier
  if (o.kopie !== (bon.kopie === true)) {
    return ungueltig('Kopie-Kennzeichen widersprüchlich');
  }
  const hash = await bonHash(bon);
  if (hash === null) {
    return ungueltig('Prüfsumme nicht berechenbar (WebCrypto fehlt)');
  }
  if (hash !== o.inhaltHash) {
    return ungueltig('Bon passt nicht zur Prüfsumme des Servers');
  }
  return {
    ok: true,
    versuch: {
      versuchId: o.versuchId,
      kopie: o.kopie,
      wiederholt: o.wiederholt === true,
      inhaltHash: o.inhaltHash,
      bon: kanonischesBonmodell(bon),
    },
  };
}

/**
 * Gerätefehler → Meldung. „Sicher nicht ausgeführt“ nur mit Code aus der festen Liste, außer
 * `schluessel_anderer_inhalt`: Unter dem Schlüssel lief am Gerät schon etwas – das melden wir
 * vorsichtig als unklar.
 */
export function meldungAusFehler(f: GeraetFehler): Druckmeldung {
  const text = meldetext(f.text);
  if (!f.unklar && SICHER_NICHT_AUSGEFUEHRT.has(f.code) && f.code !== 'schluessel_anderer_inhalt') {
    return { ergebnis: 'nicht_ausgefuehrt', code: f.code, text };
  }
  return {
    ergebnis: 'unklar',
    ...(/^[a-z0-9_]{1,64}$/.test(f.code) ? { code: f.code } : {}),
    text,
  };
}

/**
 * Freitext so, wie der Server ihn annimmt: höchstens 200 UTF-16-Einheiten, ohne Steuerzeichen
 * (außer Tab/Zeilenumbruch) und ohne einzelne Ersatzhälften; nie mitten in einem Ersatzpaar gekürzt.
 */
export function meldetext(t: string): string {
  let aus = '';
  for (const z of t) {
    const c = z.codePointAt(0) ?? 0;
    const einzelneHaelfte = z.length === 1 && c >= 0xd800 && c <= 0xdfff;
    const steuer = (c < 0x20 && c !== 0x09 && c !== 0x0a && c !== 0x0d) || c === 0x7f;
    const teil = einzelneHaelfte ? '\ufffd' : steuer ? ' ' : z;
    if (aus.length + teil.length > 200) {
      break;
    }
    aus += teil;
  }
  return aus;
}

/**
 * Quelle über die S-1-Routen. [kennung] = N1-Gerätekennung dieses Geräts, für die Kasse beim
 * Server registriert – fest oder je Reservierung ermittelt (F6: `kennungAbgleichen`). Die Meldung
 * nutzt immer die Kennung, mit der reserviert wurde (sonst passte die Gerätebindung nicht).
 */
export function druckversuchQuelle(
  http: DruckversuchHttp,
  kennung: string | ((kassenId: string) => Promise<string>),
): DruckversuchQuelle {
  /** Kennung je offenem Versuch (Reservierung → Meldung), je Seitenladen. */
  const kennungJeVersuch = new Map<string, string>();
  return {
    art: 'druckversuch',
    async reserviere(kassenId, belegnummer, opt) {
      let geraetKennung: string;
      try {
        geraetKennung = typeof kennung === 'string' ? kennung : await kennung(kassenId);
      } catch (e) {
        return {
          ok: false,
          code: 'geraetekennung_fehlt',
          text: e instanceof Error ? e.message : String(e),
        };
      }
      let antwort: { status: number; daten: unknown };
      try {
        antwort = await http.post(druckversuchPfad(kassenId, belegnummer), {
          schluessel: opt.schluessel,
          geraetKennung,
          ...(opt.kopie ? { kopie: true } : {}),
        });
      } catch (e) {
        return { ok: false, code: 'netzfehler', text: e instanceof Error ? e.message : String(e) };
      }
      if (antwort.status === 200 || antwort.status === 201) {
        const r = await leseDruckversuch(antwort.daten);
        if (r.ok) {
          // bis zur Meldung dieses Versuchs (dort entfernt); nie pauschal geleert (Codex F6-R1)
          kennungJeVersuch.set(r.versuch.versuchId, geraetKennung);
        }
        return r;
      }
      const d = (antwort.daten ?? {}) as { code?: unknown; fehler?: unknown };
      return {
        ok: false,
        code: typeof d.code === 'string' ? d.code : `http_${String(antwort.status)}`,
        text:
          typeof d.fehler === 'string' ? d.fehler : `Server antwortet ${String(antwort.status)}`,
      };
    },
    async melde(kassenId, belegnummer, versuchId, inhaltHash, m) {
      const pfad = druckmeldungPfad(kassenId, belegnummer, versuchId);
      const geraetKennung =
        kennungJeVersuch.get(versuchId) ?? (typeof kennung === 'string' ? kennung : null);
      if (geraetKennung === null) {
        // Ohne die Kennung der Reservierung keine Meldung: Versuch bleibt „reserviert“ – sicher
        return;
      }
      try {
        const antwort = await http.post(pfad, {
          ergebnis: m.ergebnis,
          inhaltHash,
          geraetKennung,
          ...(m.code === undefined ? {} : { code: m.code }),
          ...(m.text === undefined ? {} : { text: meldetext(m.text) }),
        });
        if (
          antwort.status === 422 &&
          (m.ergebnis !== 'unklar' || m.code !== undefined || m.text !== undefined)
        ) {
          // Ungültige Meldung (z. B. Code, den der Server nicht kennt): vorsichtig „unklar“
          await http.post(pfad, { ergebnis: 'unklar', inhaltHash, geraetKennung });
        }
      } catch {
        // Versuch bleibt „reserviert“ und sperrt das Original – sicher
      } finally {
        // Genau ein Meldeversuch je Versuch (§ 12): danach ist die Kennung weg; eine gescheiterte
        // Meldung wird nicht wiederholt, der Versuch bleibt „reserviert“ (Codex F6-R2 Hinweis).
        kennungJeVersuch.delete(versuchId);
      }
    },
  };
}

/** Texte zu den 409-Codes des Servers (Druck unterbleibt immer). */
export function druckversuchAbgelehntText(code: string, text: string): string {
  switch (code) {
    case 'versuch_abgeschlossen':
      return 'Für diesen Beleg lief schon ein Druck – bei Bedarf Nachdruck (trägt „KOPIE“)';
    case 'versuch_anderes_geraet':
      return 'Dieser Druck gehört zu einem anderen Gerät – hier bei Bedarf Nachdruck';
    case 'schluessel_anderer_inhalt':
      return 'Druckauftrag passt nicht zum Beleg – bei Bedarf Nachdruck';
    case 'geraetekennung_unbekannt':
    case 'geraetekennung_fehlt':
      return 'Gerät ist für diese Kasse nicht eingerichtet (Gerätekennung) – Verbindung prüfen, dann Nachdruck';
    default:
      return text;
  }
}
